import { type ChangeEvent } from 'react';
import type { MddField, FieldValue } from '../../types.js';

interface Props {
  field: MddField;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  readOnly?: boolean;
}

export default function FieldRenderer({ field, value, onChange, readOnly = false }: Props) {
  const base = 'w-full px-3 py-2 rounded-lg border border-line bg-white text-sm text-ink focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/10 transition-all disabled:bg-gray-50 disabled:text-ink-3';

  switch (field.type) {
    case 'char':
      return (
        <input
          type="text"
          className={base}
          value={(value as string) ?? ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          disabled={readOnly}
        />
      );

    case 'text':
      return (
        <textarea
          className={`${base} resize-none`}
          rows={3}
          value={(value as string) ?? ''}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
          disabled={readOnly}
        />
      );

    case 'integer':
      return (
        <input
          type="number"
          step="1"
          className={base}
          value={(value as number) ?? ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(parseInt(e.target.value, 10))}
          disabled={readOnly}
        />
      );

    case 'float':
      return (
        <input
          type="number"
          step="0.01"
          className={base}
          value={(value as number) ?? ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(parseFloat(e.target.value))}
          disabled={readOnly}
        />
      );

    case 'percent': {
      const pct = (value as number) ?? 0;
      return (
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={0} max={100} step={1}
            className="flex-1"
            style={{ '--val': `${pct}%` } as React.CSSProperties}
            value={pct}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(parseInt(e.target.value, 10))}
            disabled={readOnly}
          />
          <span className="text-sm font-mono text-ink-2 w-10 text-right">{pct}%</span>
        </div>
      );
    }

    case 'money':
      return (
        <div className="relative">
          <input
            type="number"
            step="100"
            className={`${base} pr-8`}
            value={(value as number) ?? ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(parseFloat(e.target.value))}
            disabled={readOnly}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-3 text-sm">€</span>
        </div>
      );

    case 'date':
      return (
        <input
          type="date"
          className={base}
          value={(value as string) ?? ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          disabled={readOnly}
        />
      );

    case 'boolean': {
      const on = Boolean(value);
      return (
        <button
          type="button"
          onClick={() => !readOnly && onChange(!on)}
          className={`relative w-9 h-5 rounded-full transition-colors ${on ? 'bg-brand' : 'bg-gray-300'} ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${on ? 'translate-x-4' : ''}`} />
        </button>
      );
    }

    case 'selection':
      return (
        <select
          className={base}
          value={(value as string) ?? ''}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
          disabled={readOnly}
        >
          <option value="">—</option>
          {field.options?.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );

    case 'radio':
      return (
        <div className="flex flex-wrap gap-2">
          {field.options?.map(o => (
            <button
              key={o}
              type="button"
              onClick={() => !readOnly && onChange(o)}
              className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${value === o ? 'bg-brand border-brand text-white' : 'bg-white border-line text-ink-2 hover:border-brand/40'}`}
            >
              {o}
            </button>
          ))}
        </div>
      );

    case 'multiselect':
    case 'checkbox_group': {
      const selected = (value as string[]) ?? [];
      return (
        <div className="flex flex-wrap gap-1.5">
          {field.options?.map(o => {
            const active = selected.includes(o);
            return (
              <button
                key={o}
                type="button"
                onClick={() => {
                  if (readOnly) return;
                  onChange(active ? selected.filter(s => s !== o) : [...selected, o]);
                }}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border transition-all ${
                  active ? 'bg-brand-soft border-brand/30 text-brand-dark' : 'bg-white border-line text-ink-2 hover:border-brand/30'
                }`}
              >
                {active && <span>✓</span>}
                {o}
              </button>
            );
          })}
        </div>
      );
    }

    case 'file':
      return (
        <input
          type="file"
          className="text-sm text-ink-2 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-brand-soft file:text-brand file:font-medium file:cursor-pointer"
          disabled={readOnly}
        />
      );

    case 'computed':
      return (
        <div className={`${base} bg-gray-50 text-ink-3 font-mono text-xs`}>
          {String(value ?? '—')}
        </div>
      );

    case 'chart':
      return (
        <div className="h-24 rounded-lg border border-line bg-gray-50 flex items-center justify-center text-xs text-ink-3">
          Graphique — données à intégrer
        </div>
      );

    default:
      return (
        <input
          type="text"
          className={base}
          value={(value as string) ?? ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          disabled={readOnly}
        />
      );
  }
}
